import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { card, h1, sub, input, mono, btn, pill, RGB, Icon, ICONS, errMsg, timeAgo } from '../ext/extUi';
import { cacheGet, cacheSet } from '../../utils/clientCache';

// Web file manager (WinSCP / FileZilla in the browser) over the server's own SFTP/FTP login.
// Backend: server/routes/serverFiles.js. Uploads go in 8 MB chunks (resumable, under Cloudflare's
// request limits); downloads use one-time tickets so no login token ends up in a URL.

const CHUNK = 8 * 1024 * 1024;
const FI = {
  folder: 'M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z',
  file: 'M7 3h7l5 5v11a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2z',
  upload: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12',
  up: 'M5 15l7-7 7 7',
  home: 'M3 12l9-9 9 9M5 10v10h14V10',
  history: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  x: 'M6 18L18 6M6 6l12 12',
};
const errOf = (e, fb) => (e && e.response && e.response.data && e.response.data.error) || errMsg(e, fb);
const codeOf = (e) => e && e.response && e.response.data && e.response.data.code;
const fmtSize = (n) => (n == null ? '' : n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KB` : n < 1073741824 ? `${(n / 1048576).toFixed(1)} MB` : `${(n / 1073741824).toFixed(2)} GB`);
const join = (dir, name) => (dir === '/' ? `/${name}` : `${dir}/${name}`);
// Any file up to 1 MB can be opened in the editor — the server checks the content and refuses real
// binaries. Only these obviously-binary types download on double-click instead of opening.
const EDIT_MAX = 50 * 1024 * 1024;
const BINARY_EXT = /\.(vpk|bsp|dem|nav|vtf|vmt_c|vtex_c|vmdl_c|vmat_c|vsnd_c|mdl|vvd|vtx|phy|zip|rar|7z|gz|tgz|bz2|xz|tar|so|dll|exe|bin|dat|db|sqlite|png|jpe?g|gif|webp|bmp|ico|mp3|wav|ogg|mp4|webm|avi|pdf|ttf|otf|woff2?)$/i;
const canEdit = (e) => e.type !== 'dir' && e.size <= EDIT_MAX;

/** Collect files from a drop, keeping folder structure (relative paths). */
async function filesFromDrop(dt) {
  const out = [];
  const walk = (entry, prefix) => new Promise((resolve) => {
    if (entry.isFile) entry.file((f) => { out.push({ file: f, rel: prefix + f.name }); resolve(); }, () => resolve());
    else if (entry.isDirectory) {
      const reader = entry.createReader(); const all = [];
      const read = () => reader.readEntries(async (batch) => {
        if (!batch.length) { for (const e of all) await walk(e, `${prefix}${entry.name}/`); resolve(); } else { all.push(...batch); read(); }
      }, () => resolve());
      read();
    } else resolve();
  });
  const entries = [...(dt.items || [])].map(i => (i.webkitGetAsEntry ? i.webkitGetAsEntry() : null)).filter(Boolean);
  if (entries.length) { for (const e of entries) await walk(e, ''); return out; }
  return [...(dt.files || [])].map(f => ({ file: f, rel: f.name }));
}

function Editor({ serverId, file, force, onClose, onSaved, onDownload }) {
  const { t } = useTranslation();
  const [doc, setDoc] = useState(null);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const dirty = doc && text !== doc.content;
  const load = useCallback(async () => {
    setError('');
    try { const r = await axios.get(`/api/servers/${serverId}/files/content`, { params: { path: file }, timeout: 0 }); setDoc(r.data); setText(r.data.content); }
    catch (e) { setError(errOf(e, t('files.openFailed', 'Could not open the file'))); }
  }, [serverId, file, t]);
  useEffect(() => { load(); }, [load]);
  const save = useCallback(async () => {
    if (!doc || saving) return;
    setSaving(true); setError('');
    try {
      // raw text body (not JSON): no escaping overhead for big files
      const r = await axios.put(`/api/servers/${serverId}/files/content`, text, {
        headers: { 'Content-Type': 'text/plain' }, timeout: 0,
        params: { path: file, mtime: doc.mtime, size: doc.size, ...(force ? { force: 1 } : {}) },
      });
      setDoc({ ...doc, content: text, mtime: r.data.mtime, size: r.data.size });
      onSaved();
    } catch (e) { setError(errOf(e, t('files.saveFailed', 'Save failed'))); }
    finally { setSaving(false); }
  }, [doc, saving, serverId, file, text, force, onSaved, t]);
  const close = () => { if (!dirty || window.confirm(t('files.discardChanges', 'Discard unsaved changes?'))) onClose(); };
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div style={{ ...card, margin: 0, width: 'min(1000px, 100%)', height: 'min(760px, 82vh)', display: 'flex', flexDirection: 'column', borderRadius: 'var(--radius-xl, 16px)', boxSizing: 'border-box' }}
        onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); save(); } if (e.key === 'Escape') close(); }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem' }}>
          <Icon d={FI.file} />
          <span style={{ ...mono, color: 'var(--text-primary)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file}</span>
          {dirty && <span style={pill(RGB.amber)}>{t('files.unsaved', 'Unsaved')}</span>}
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Ctrl+S</span>
          <button type="button" style={btn(RGB.green, true)} disabled={!dirty || saving} onClick={save}>{saving ? t('common.loading', 'Loading...') : t('common.save', 'Save')}</button>
          <button type="button" style={btn(RGB.gray)} onClick={close}>{t('common.close', 'Close')}</button>
        </div>
        {error && (
          <div style={{ marginBottom: '0.5rem', fontSize: '0.78rem', color: 'var(--accent-red)', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {error}{/changed/i.test(error) && <button type="button" style={btn(RGB.blue)} onClick={load}>{t('files.reload', 'Reload')}</button>}
            {!doc && onDownload && <button type="button" style={btn(RGB.blue)} onClick={() => { onDownload(file); onClose(); }}><Icon d={ICONS.download} /> {t('files.download', 'Download')}</button>}
          </div>
        )}
        {doc ? (
          <textarea value={text} onChange={e => setText(e.target.value)} spellCheck={false} autoFocus
            style={{ ...input, ...mono, flex: 1, minHeight: 0, resize: 'none', lineHeight: 1.5, whiteSpace: 'pre', overflow: 'auto', tabSize: 4, fontSize: '0.8rem', boxSizing: 'border-box' }} />
        ) : !error && <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{t('common.loading', 'Loading...')}</div>}
      </div>
    </div>,
    document.body,
  );
}

export default function FileManager() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [servers, setServers] = useState([]);
  const [dir, setDir] = useState(null);
  const [listing, setListing] = useState(null);
  const [pathInput, setPathInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [revalidating, setRevalidating] = useState(false); // cached folder on screen, fresh one on its way
  const [hovered, setHovered] = useState(null); // row under the mouse
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [lastClicked, setLastClicked] = useState(null);
  const [sort, setSort] = useState({ key: 'name', asc: true });
  const [renaming, setRenaming] = useState(null);
  const [renameTo, setRenameTo] = useState('');
  const [editing, setEditing] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [menu, setMenu] = useState(null);
  const [allowLive, setAllowLive] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [audit, setAudit] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const fileInput = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ left: 0, top: 0 });
  // Place the menu at the cursor, flipped/clamped so it always stays inside the window.
  React.useLayoutEffect(() => {
    if (!menu || !menuRef.current) return;
    const { offsetWidth: w, offsetHeight: h } = menuRef.current;
    // The panel scales the page with CSS zoom on <html> (--app-zoom, index.css): mouse coordinates are
    // screen pixels, fixed positions are zoomed CSS pixels — convert before placing the menu.
    const z = parseFloat(getComputedStyle(document.documentElement).zoom) || 1;
    const x = menu.x / z, y = menu.y / z, vw = window.innerWidth / z, vh = window.innerHeight / z;
    const pad = 8;
    let left = x, top = y;
    if (left + w + pad > vw) left = Math.max(pad, x - w);
    if (top + h + pad > vh) top = Math.max(pad, y - h);
    setMenuPos({ left, top });
  }, [menu]);
  useEffect(() => {
    if (!menu) return undefined;
    const closeMenu = (e) => { if (!menuRef.current || !menuRef.current.contains(e.target)) setMenu(null); };
    const onKey = (e) => { if (e.key === 'Escape') setMenu(null); };
    const onScroll = () => setMenu(null);
    document.addEventListener('mousedown', closeMenu);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', closeMenu);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [menu]);
  const ctrls = useRef(new Map());
  const force = allowLive;

  useEffect(() => {
    const cached = cacheGet('files:servers');
    if (cached) setServers(cached);
    axios.get('/api/servers', { params: { limit: 1000 } })
      .then(r => {
        const list = (r.data.data || []).filter(s => s.has_backup && !(typeof s.ip === 'string' && s.ip.startsWith('lan.')));
        setServers(list); cacheSet('files:servers', list);
      })
      .catch(() => { });
  }, []);

  // Folder listings are cached per server + path in the browser: a folder opened before shows
  // instantly, then refreshes in the background. After a write, refresh() always asks the server.
  const reqSeq = useRef(0);
  const load = useCallback(async (p, { fresh = false } = {}) => {
    const seq = ++reqSeq.current;
    const key = `files:${id}:${p || '~'}`;
    const cached = fresh ? undefined : cacheGet(key);
    const show = (d, keepSelection) => {
      setListing(d); setDir(d.path); setPathInput(d.path);
      if (!keepSelection) { setSelected(new Set()); setRenaming(null); }
    };
    setError('');
    if (cached) { show(cached, false); setLoading(false); setRevalidating(true); } else setLoading(true);
    try {
      const r = await axios.get(`/api/servers/${id}/files`, { params: p ? { path: p } : {} });
      cacheSet(key, r.data);
      if (!p) cacheSet(`files:${id}:${r.data.path}`, r.data); // the start folder is also reachable by its path
      if (seq === reqSeq.current) show(r.data, !!cached || fresh); // stale answers for a folder we left are dropped
    } catch (e) { if (seq === reqSeq.current) setError(errOf(e, t('files.loadFailed', 'Could not open this folder'))); }
    finally { if (seq === reqSeq.current) { setLoading(false); setRevalidating(false); } }
  }, [id, t]);

  useEffect(() => { setListing(null); setDir(null); load(null); }, [id, load]);
  const refresh = () => load(dir, { fresh: true });

  const server = servers.find(s => String(s.id) === String(id));
  const serverLabel = (s) => (s.lan ? `${s.lan.event} / ${s.lan.machine} · ${s.port}` : `${s.name} (${s.ip}:${s.port})`);

  const entries = useMemo(() => {
    const list = listing ? [...listing.entries] : [];
    const k = sort.key, dirSign = (e) => (e.type === 'dir' ? 0 : 1);
    list.sort((a, b) => {
      if (dirSign(a) !== dirSign(b)) return dirSign(a) - dirSign(b);
      let v = k === 'size' ? (a.size || 0) - (b.size || 0) : k === 'modified' ? String(a.modified).localeCompare(String(b.modified)) : a.name.localeCompare(b.name, undefined, { numeric: true });
      return sort.asc ? v : -v;
    });
    return list;
  }, [listing, sort]);

  const crumbs = useMemo(() => {
    if (!dir) return [];
    const parts = dir.split('/').filter(Boolean);
    return [{ name: '/', path: '/' }, ...parts.map((p, i) => ({ name: p, path: '/' + parts.slice(0, i + 1).join('/') }))];
  }, [dir]);

  const act = async (fn, fallback) => {
    setError('');
    try { await fn(); }
    catch (e) {
      if (codeOf(e) === 'server_live') setError(t('files.liveBlocked', 'This server is in a live match. Tick "Allow changes during the live match" to continue.'));
      else setError(errOf(e, fallback));
    }
  };

  const open = (e) => {
    const p = join(dir, e.name);
    if (e.type === 'dir' || e.type === 'link') load(p);
    else if (canEdit(e) && !BINARY_EXT.test(e.name)) setEditing(p);
    else download([p]);
  };

  const download = async (paths) => {
    for (const p of paths) {
      await act(async () => {
        const r = await axios.post(`/api/servers/${id}/files/ticket`, { path: p });
        const a = document.createElement('a'); a.href = r.data.url; a.download = ''; document.body.appendChild(a); a.click(); a.remove();
      }, t('files.downloadFailed', 'Download failed'));
    }
  };

  const mkdir = async () => {
    const name = window.prompt(t('files.newFolderName', 'New folder name'));
    if (!name || !name.trim()) return;
    await act(async () => { await axios.post(`/api/servers/${id}/files/mkdir`, { path: join(dir, name.trim()), force }); refresh(); }, t('files.mkdirFailed', 'Could not create the folder'));
  };

  const doRename = async (from) => {
    const to = renameTo.trim();
    setRenaming(null);
    if (!to || to === from.split('/').pop()) return;
    await act(async () => { await axios.post(`/api/servers/${id}/files/rename`, { from, to: join(dir, to), force }); refresh(); }, t('files.renameFailed', 'Rename failed'));
  };

  const doDelete = async (paths) => {
    setConfirmDelete(null);
    await act(async () => {
      const r = await axios.post(`/api/servers/${id}/files/delete`, { paths, recursive: true, force }, { validateStatus: s => s === 200 || s === 207 });
      const bad = (r.data.results || []).filter(x => !x.ok);
      if (bad.length) setError(`${t('files.deleteSomeFailed', 'Some items could not be deleted')}: ${bad.map(b => `${b.path.split('/').pop()} (${b.error})`).join(', ')}`);
      refresh();
    }, t('files.deleteFailed', 'Delete failed'));
  };

  // ── uploads ──
  const patchT = (key, p) => setTransfers(list => list.map(x => (x.key === key ? { ...x, ...p } : x)));
  const runUpload = useCallback(async (tr, overwrite = false) => {
    const ctrl = new AbortController();
    ctrls.current.set(tr.key, ctrl);
    let offset = tr.received || 0;
    const started = Date.now(), startOffset = offset;
    patchT(tr.key, { status: 'uploading', error: null });
    try {
      while (offset < tr.file.size || (tr.file.size === 0 && offset === 0)) {
        const base = offset;
        const blob = tr.file.slice(base, base + CHUNK);
        const params = { path: tr.target, offset: base, total: tr.file.size };
        if (overwrite) params.overwrite = 1;
        if (force) params.force = 1;
        let r;
        try {
          r = await axios.put(`/api/servers/${id}/files/upload`, blob, {
            params, signal: ctrl.signal, timeout: 0, headers: { 'Content-Type': 'application/octet-stream' },
            onUploadProgress: (ev) => {
              const done = base + (ev.loaded || 0);
              const secs = (Date.now() - started) / 1000;
              patchT(tr.key, { progress: done, speed: secs > 0.5 ? (done - startOffset) / secs : null });
            },
          });
        } catch (e) {
          if (codeOf(e) === 'resume' && e.response.data.received != null) { offset = e.response.data.received; continue; }
          throw e;
        }
        offset = r.data.received;
        patchT(tr.key, { received: offset, progress: offset });
        if (r.data.done) break;
      }
      patchT(tr.key, { status: 'done', progress: tr.file.size });
      if (tr.dir === dir) refresh();
    } catch (e) {
      if (axios.isCancel(e) || e.name === 'CanceledError') {
        patchT(tr.key, { status: 'cancelled' });
        axios.delete(`/api/servers/${id}/files/upload`, { params: { path: tr.target } }).catch(() => { });
      } else if (codeOf(e) === 'exists') patchT(tr.key, { status: 'exists' });
      else if (codeOf(e) === 'server_live') patchT(tr.key, { status: 'error', error: t('files.liveBlockedShort', 'Live match - allow changes first') });
      else patchT(tr.key, { status: 'error', error: errOf(e, t('files.uploadFailed', 'Upload failed')), received: offset });
    } finally { ctrls.current.delete(tr.key); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, dir, force]);

  const startUploads = async (items) => {
    if (!items.length) return;
    // create sub-folders of a dropped folder first (409 = already there)
    const dirs = [...new Set(items.map(i => i.rel.split('/').slice(0, -1).join('/')).filter(Boolean))].sort((a, b) => a.length - b.length);
    for (const d of dirs) { try { await axios.post(`/api/servers/${id}/files/mkdir`, { path: join(dir, d), force }); } catch (_) { /* exists */ } }
    const list = items.map(({ file, rel }) => ({ key: `${Date.now()}-${Math.random()}`, file, name: rel, dir, target: join(dir, rel), progress: 0, received: 0, status: 'queued' }));
    setTransfers(prev => [...list, ...prev]);
    for (let i = 0; i < list.length; i += 2) await Promise.all(list.slice(i, i + 2).map(tr => runUpload(tr)));
  };

  const onDrop = async (e) => {
    e.preventDefault(); setDragOver(false);
    startUploads(await filesFromDrop(e.dataTransfer));
  };

  const loadAudit = useCallback(async () => { try { const r = await axios.get(`/api/servers/${id}/files/audit`); setAudit(r.data.data || []); } catch (_) { } }, [id]);
  useEffect(() => { if (showAudit) loadAudit(); }, [showAudit, loadAudit]);

  const clickRow = (e, name) => {
    setMenu(null);
    const next = new Set(e.ctrlKey || e.metaKey ? selected : []);
    if (e.shiftKey && lastClicked) {
      const names = entries.map(x => x.name);
      const [a, b] = [names.indexOf(lastClicked), names.indexOf(name)].sort((x, y) => x - y);
      names.slice(a, b + 1).forEach(n => next.add(n));
    } else if ((e.ctrlKey || e.metaKey) && next.has(name)) next.delete(name); else next.add(name);
    setSelected(next); setLastClicked(name);
  };
  const selPaths = [...selected].map(n => join(dir, n));
  const selEntries = entries.filter(e => selected.has(e.name));
  const sortBtn = (key, label) => (
    <button type="button" onClick={() => setSort(s => ({ key, asc: s.key === key ? !s.asc : true }))}
      style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', justifySelf: 'start', textAlign: 'left', color: 'var(--text-muted)', font: 'inherit', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      {label}{sort.key === key ? (sort.asc ? ' ▲' : ' ▼') : ''}
    </button>
  );

  return (
    <div onClick={() => setMenu(null)}>
      <div className="page-header" style={{ marginBottom: '1rem', paddingBottom: '0.75rem' }}>
        <Link to="/admin/servers" style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
          <Icon d={ICONS.back} size={12} /> {t('files.backToServers', 'Servers')}
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.35rem' }}>
          <h1 style={h1}>{t('files.title', 'File Manager')}</h1>
          <select value={id} onChange={e => navigate(`/admin/servers/${e.target.value}/files`)} style={{ ...input, width: 'auto', minWidth: 260 }}>
            {!server && <option value={id}>{t('files.serverN', 'Server #{{id}}', { id })}</option>}
            {servers.filter(s => s.lan).length > 0 && <optgroup label={t('files.lanServers', 'LAN servers')}>{servers.filter(s => s.lan).map(s => <option key={s.id} value={s.id}>{serverLabel(s)}</option>)}</optgroup>}
            {servers.filter(s => !s.lan).length > 0 && <optgroup label={t('files.onlineServers', 'Online servers')}>{servers.filter(s => !s.lan).map(s => <option key={s.id} value={s.id}>{serverLabel(s)}</option>)}</optgroup>}
          </select>
          {listing && listing.protocol && <span style={pill(RGB.gray)}>{listing.protocol.toUpperCase()}</span>}
          <span style={{ flex: 1 }} />
          <button type="button" style={btn(showAudit ? RGB.blue : RGB.gray)} onClick={(e) => { e.stopPropagation(); setShowAudit(v => !v); }}><Icon d={FI.history} /> {t('files.activity', 'Activity')}</button>
        </div>
        <p style={{ ...sub, margin: '0.35rem 0 0' }}>{t('files.subtitle', 'Browse, upload, download and edit files on this server through its SFTP/FTP login. Every change is recorded in Activity.')}</p>
      </div>

      {listing && listing.live_match && (
        <div style={{ ...card, borderColor: 'rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.06)', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', padding: '0.75rem 1rem' }}>
          <span style={pill(RGB.amber)}>{t('files.liveMatch', 'Live match')}</span>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{t('files.liveMatchHint', 'This server is running a live match. Changing files now can affect it.')}</span>
          <span style={{ flex: 1 }} />
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={allowLive} onChange={e => setAllowLive(e.target.checked)} style={{ accentColor: '#f59e0b' }} />
            {t('files.allowLive', 'Allow changes during the live match')}
          </label>
        </div>
      )}

      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...card, padding: '0.75rem 1rem' }}>
            {/* toolbar */}
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
              <button type="button" title={t('files.up', 'Up')} style={btn(RGB.gray)} disabled={!listing || !listing.parent} onClick={() => load(listing.parent)}><Icon d={FI.up} /></button>
              <button type="button" title={t('files.home', 'Server folder')} style={btn(RGB.gray)} disabled={!listing} onClick={() => load(listing.start)}><Icon d={FI.home} /></button>
              <button type="button" title={revalidating ? t('files.updating', 'Updating…') : t('files.refresh', 'Refresh')} style={{ ...btn(RGB.gray), opacity: revalidating || loading ? 0.55 : 1, transition: 'opacity 0.2s' }} disabled={!dir} onClick={refresh}><Icon d={ICONS.refresh} /></button>
              <form onSubmit={(e) => { e.preventDefault(); load(pathInput); }} style={{ flex: '1 1 280px', display: 'flex' }}>
                <input value={pathInput} onChange={e => setPathInput(e.target.value)} spellCheck={false} style={{ ...input, ...mono, padding: '0.4rem 0.65rem' }} />
              </form>
              <button type="button" style={btn(RGB.green, true)} disabled={!dir} onClick={() => fileInput.current && fileInput.current.click()}><Icon d={FI.upload} /> {t('files.upload', 'Upload')}</button>
              <input ref={fileInput} type="file" multiple style={{ display: 'none' }} onChange={(e) => { startUploads([...e.target.files].map(f => ({ file: f, rel: f.name }))); e.target.value = ''; }} />
              <button type="button" style={btn(RGB.blue)} disabled={!dir} onClick={mkdir}><Icon d={ICONS.plus} /> {t('files.newFolder', 'New folder')}</button>
            </div>
            {/* breadcrumbs */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.15rem', fontSize: '0.78rem', marginBottom: '0.5rem' }}>
              {crumbs.map((c, i) => (
                <React.Fragment key={c.path}>
                  {i > 1 && <span style={{ color: 'var(--text-muted)' }}>/</span>}
                  <button type="button" onClick={() => load(c.path)} style={{ background: 'none', border: 0, cursor: 'pointer', color: i === crumbs.length - 1 ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: i === crumbs.length - 1 ? 700 : 500, padding: '0.1rem 0.25rem', ...mono, fontSize: '0.76rem' }}>{c.name}</button>
                </React.Fragment>
              ))}
            </div>
            {/* selection bar */}
            {selected.size > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.45rem 0.6rem', marginBottom: '0.4rem', borderRadius: 'var(--radius-md, 8px)', background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.15)', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{t('files.selectedN', '{{n}} selected', { n: selected.size })}</span>
                <span style={{ flex: 1 }} />
                {selEntries.some(e => e.type !== 'dir') && <button type="button" style={btn(RGB.blue)} onClick={() => download(selEntries.filter(e => e.type !== 'dir').map(e => join(dir, e.name)))}><Icon d={ICONS.download} /> {t('files.download', 'Download')}</button>}
                {selected.size === 1 && <button type="button" style={btn(RGB.gray)} onClick={() => { setRenaming(selPaths[0]); setRenameTo([...selected][0]); }}><Icon d={ICONS.edit} /> {t('files.rename', 'Rename')}</button>}
                <button type="button" style={btn(RGB.red)} onClick={() => setConfirmDelete(selPaths)}><Icon d={ICONS.trash} /> {t('files.delete', 'Delete')}</button>
              </div>
            )}
            {confirmDelete && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.55rem 0.7rem', marginBottom: '0.4rem', borderRadius: 'var(--radius-md, 8px)', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-primary)' }}>
                  {t('files.confirmDelete', 'Delete {{n}} item(s)? Folders are deleted with everything inside. This cannot be undone.', { n: confirmDelete.length })}
                </span>
                <span style={{ flex: 1 }} />
                <button type="button" style={btn(RGB.red, true)} onClick={() => doDelete(confirmDelete)}>{t('files.delete', 'Delete')}</button>
                <button type="button" style={btn(RGB.gray)} onClick={() => setConfirmDelete(null)}>{t('common.cancel', 'Cancel')}</button>
              </div>
            )}
            {error && <div style={{ fontSize: '0.78rem', color: 'var(--accent-red)', margin: '0.2rem 0 0.5rem' }}>{error}</div>}

            {/* file list + drop zone */}
            <div onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop}
              style={{ position: 'relative', borderRadius: 'var(--radius-md, 8px)', border: `1px dashed ${dragOver ? 'rgba(34,197,94,0.6)' : 'transparent'}`, background: dragOver ? 'rgba(34,197,94,0.05)' : 'transparent', minHeight: 240 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 170px 110px', gap: '0.5rem', padding: '0.35rem 0.6rem', borderBottom: '1px solid var(--border-subtle)' }}>
                {sortBtn('name', t('files.name', 'Name'))}{sortBtn('size', t('files.size', 'Size'))}{sortBtn('modified', t('files.modified', 'Modified'))}
                <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('files.permissions', 'Permissions')}</span>
              </div>
              {loading && !listing && <div style={{ padding: '1rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>{t('common.loading', 'Loading...')}</div>}
              {listing && entries.length === 0 && <div style={{ padding: '1.5rem', textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-muted)' }}>{t('files.empty', 'Empty folder - drop files here to upload')}</div>}
              {entries.map(e => {
                const p = join(dir, e.name), sel = selected.has(e.name);
                // one click opens folders and text files (editor); Ctrl/Shift-click still selects
                const oneClick = e.type === 'dir' || e.type === 'link' || (canEdit(e) && !BINARY_EXT.test(e.name));
                const hov = hovered === e.name;
                return (
                  <div key={e.name} onClick={(ev) => {
                    ev.stopPropagation();
                    if (oneClick && !ev.ctrlKey && !ev.metaKey && !ev.shiftKey && renaming !== p) { setMenu(null); open(e); return; }
                    clickRow(ev, e.name);
                  }} onDoubleClick={() => { if (!oneClick) open(e); }}
                    onMouseEnter={() => setHovered(e.name)} onMouseLeave={() => setHovered(h => (h === e.name ? null : h))}
                    onContextMenu={(ev) => { ev.preventDefault(); ev.stopPropagation(); if (!sel) { setSelected(new Set([e.name])); setLastClicked(e.name); } setMenu({ x: ev.clientX, y: ev.clientY, entry: e }); }}
                    style={{ display: 'grid', gridTemplateColumns: '1fr 110px 170px 110px', gap: '0.5rem', alignItems: 'center', padding: '0.4rem 0.6rem', cursor: oneClick ? 'pointer' : 'default', userSelect: 'none',
                      borderBottom: '1px solid var(--border-subtle)', transition: 'background 0.1s',
                      background: sel ? 'rgba(59,130,246,0.1)' : hov ? 'rgba(255,255,255,0.045)' : 'transparent' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0, color: e.type === 'dir' ? '#60a5fa' : 'var(--text-secondary)' }}>
                      <Icon d={e.type === 'dir' ? FI.folder : FI.file} size={15} />
                      {renaming === p ? (
                        <input autoFocus value={renameTo} onChange={ev => setRenameTo(ev.target.value)} onClick={ev => ev.stopPropagation()}
                          onKeyDown={ev => { if (ev.key === 'Enter') doRename(p); if (ev.key === 'Escape') setRenaming(null); }} onBlur={() => doRename(p)}
                          style={{ ...input, ...mono, padding: '0.15rem 0.4rem', fontSize: '0.8rem' }} />
                      ) : (
                        <span style={{ fontSize: '0.82rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name}{e.type === 'link' ? ' ↗' : ''}</span>
                      )}
                    </span>
                    <span style={{ ...mono, fontSize: '0.74rem', color: 'var(--text-muted)' }}>{e.type === 'dir' ? '' : fmtSize(e.size)}</span>
                    <span style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>{e.modified ? new Date(e.modified).toLocaleString() : ''}</span>
                    <span style={{ ...mono, fontSize: '0.72rem', color: 'var(--text-muted)' }}>{e.mode}</span>
                  </div>
                );
              })}
              {dragOver && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', color: '#22c55e', fontWeight: 700, fontSize: '0.9rem' }}>{t('files.dropToUpload', 'Drop to upload into {{dir}}', { dir })}</div>}
            </div>
          </div>

          {/* transfers */}
          {transfers.length > 0 && (
            <div style={{ ...card, padding: '0.75rem 1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.4rem' }}>
                <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('files.transfers', 'Transfers')}</span>
                <span style={{ flex: 1 }} />
                <button type="button" style={btn(RGB.gray)} onClick={() => setTransfers(l => l.filter(x => x.status === 'uploading' || x.status === 'queued'))}>{t('files.clearFinished', 'Clear finished')}</button>
              </div>
              {transfers.map(tr => {
                const pct = tr.file.size ? Math.min(100, Math.round((tr.progress / tr.file.size) * 100)) : (tr.status === 'done' ? 100 : 0);
                const color = { done: RGB.green, error: RGB.red, cancelled: RGB.gray, exists: RGB.amber }[tr.status] || RGB.blue;
                return (
                  <div key={tr.key} style={{ padding: '0.4rem 0', borderTop: '1px solid var(--border-subtle)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ ...mono, fontSize: '0.78rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '50%' }}>{tr.name}</span>
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{fmtSize(tr.progress)} / {fmtSize(tr.file.size)}{tr.status === 'uploading' && tr.speed ? ` · ${fmtSize(tr.speed)}/s` : ''}</span>
                      <span style={pill(color)}>{t(`files.status.${tr.status}`, tr.status)}</span>
                      {tr.error && <span style={{ fontSize: '0.72rem', color: 'var(--accent-red)' }}>{tr.error}</span>}
                      <span style={{ flex: 1 }} />
                      {tr.status === 'uploading' && <button type="button" style={btn(RGB.gray)} onClick={() => { const c = ctrls.current.get(tr.key); if (c) c.abort(); }}>{t('common.cancel', 'Cancel')}</button>}
                      {tr.status === 'error' && <button type="button" style={btn(RGB.blue)} onClick={() => runUpload(tr)}>{t('files.retry', 'Retry')}</button>}
                      {tr.status === 'exists' && (
                        <>
                          <button type="button" style={btn(RGB.amber, true)} onClick={() => runUpload({ ...tr, received: 0 }, true)}>{t('files.overwrite', 'Overwrite')}</button>
                          <button type="button" style={btn(RGB.gray)} onClick={() => patchT(tr.key, { status: 'cancelled' })}>{t('files.skip', 'Skip')}</button>
                        </>
                      )}
                    </div>
                    <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.06)', marginTop: '0.3rem', overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: `rgb(${color})`, transition: 'width 0.2s' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {showAudit && (
          <div style={{ ...card, width: 340, flexShrink: 0, padding: '0.75rem 1rem', maxHeight: '75vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('files.activity', 'Activity')}</span>
              <span style={{ flex: 1 }} />
              <button type="button" style={btn(RGB.gray)} onClick={loadAudit}><Icon d={ICONS.refresh} /></button>
            </div>
            {audit.length === 0 && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{t('files.noActivity', 'No file changes yet.')}</div>}
            {audit.map(a => (
              <div key={a.id} style={{ padding: '0.4rem 0', borderTop: '1px solid var(--border-subtle)', fontSize: '0.74rem' }}>
                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                  <span style={pill(a.ok ? RGB.blue : RGB.red)}>{t(`files.op.${a.op}`, a.op)}</span>
                  <span style={{ color: 'var(--text-secondary)' }}>{a.username || '—'}</span>
                  <span style={{ flex: 1 }} />
                  <span style={{ color: 'var(--text-muted)' }}>{timeAgo(a.at)}</span>
                </div>
                <div style={{ ...mono, fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2, wordBreak: 'break-all' }}>{a.path}{a.path2 ? ` → ${a.path2}` : ''}{a.bytes != null ? ` · ${fmtSize(a.bytes)}` : ''}</div>
                {!a.ok && a.error && <div style={{ color: 'var(--accent-red)', marginTop: 2 }}>{a.error}</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      {menu && createPortal(
        <div ref={menuRef} style={{ position: 'fixed', left: menuPos.left, top: menuPos.top, zIndex: 900, background: 'var(--bg-elevated, #1a1a24)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md, 8px)', padding: '0.3rem', minWidth: 170, boxShadow: '0 12px 40px rgba(0,0,0,0.5)' }}
          onClick={e => e.stopPropagation()}>
          {[
            menu.entry.type === 'dir' && { label: t('files.openFolder', 'Open'), run: () => open(menu.entry) },
            canEdit(menu.entry) && { label: t('files.edit', 'Edit'), run: () => setEditing(join(dir, menu.entry.name)) },
            menu.entry.type !== 'dir' && { label: t('files.download', 'Download'), run: () => download(selPaths.length ? selPaths.filter(p => entries.find(e => join(dir, e.name) === p && e.type !== 'dir')) : [join(dir, menu.entry.name)]) },
            selected.size <= 1 && { label: t('files.rename', 'Rename'), run: () => { setRenaming(join(dir, menu.entry.name)); setRenameTo(menu.entry.name); } },
            { label: t('files.copyPath', 'Copy path'), run: () => navigator.clipboard && navigator.clipboard.writeText(join(dir, menu.entry.name)) },
            { label: t('files.delete', 'Delete'), danger: true, run: () => setConfirmDelete(selPaths.length ? selPaths : [join(dir, menu.entry.name)]) },
          ].filter(Boolean).map((it, i) => (
            <button key={i} type="button" onClick={() => { setMenu(null); it.run(); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 0, borderRadius: 6, padding: '0.45rem 0.7rem', cursor: 'pointer', fontSize: '0.8rem', fontFamily: 'inherit', color: it.danger ? '#ef4444' : 'var(--text-primary)' }}
              onMouseOver={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }} onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}>{it.label}</button>
          ))}
        </div>,
        document.body,
      )}

      {editing && <Editor serverId={id} file={editing} force={force} onClose={() => setEditing(null)} onSaved={refresh} onDownload={(p) => download([p])} />}
    </div>
  );
}
